import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef, Inject, Input, OnDestroy, PLATFORM_ID,
  ViewChild,
  OnInit, Renderer2, HostBinding
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { generateArray, BannerLocation, LeagueBanner, componentDestroyed, UntilDestroy } from '@mtgame/core';
import { DIRECTION_HORIZONTAL, Manager, Swipe } from '@squadette/hammerjs';
import { isPlatformBrowser } from '@angular/common';
import { LeagueHomeService } from '@core/services/league-home.service';
import { takeUntil } from 'rxjs/operators';
import { isTouchDevice } from '@mtgame/ui';

@UntilDestroy
@Component({
  selector: 'mtg-banners-slider',
  templateUrl: './banners-slider.component.html',
  styleUrls: ['./banners-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({opacity: 0}),
        animate('200ms ease-in', style({opacity: 1}))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({opacity: 0}))
      ])
    ])
  ]
})
export class BannersSliderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  location: BannerLocation;

  slides: LeagueBanner[] = [];
  current = 0;
  @ViewChild('slider', {read: ElementRef})
  slider: ElementRef;
  isBrowser = isPlatformBrowser(this.platformId);
  loaded = false;
  stopScroll = false;

  @HostBinding('class.hidden')
  get hiddenClass() {
    return this.slides.length === 0;
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private leagueHomeService: LeagueHomeService,
    private cdr: ChangeDetectorRef,
    private renderer2: Renderer2
  ) {
  }

  ngOnInit(): void {
    this.leagueHomeService.banners$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(slides => {
        this.slides = slides.filter(slide => slide.location === this.location);
        this.cdr.markForCheck();
      });
    if (this.isBrowser) {
      setTimeout(() => {
        this.loaded = true;
        this.initializeAutoScroll();
        this.cdr.markForCheck();
      }, 100);
    }
  }

  ngOnDestroy(): void {
  }

  initializeAutoScroll(): void {
    setTimeout(() => {
      this.sliderScroll();
    }, 4000);
    setTimeout(() => {
      this.renderer2.listen(this.slider.nativeElement, 'mouseover', event => {
        this.stopScroll = true;
      });
      this.renderer2.listen(this.slider.nativeElement, 'mouseleave', event => {
        this.stopScroll = false;
      });
    });
  }

  sliderScroll() {
    if (this.slider && document.visibilityState === 'visible' && !this.stopScroll && this.loaded) {
      const bounding = this.slider.nativeElement.getBoundingClientRect();
      if (
        bounding.top >= 0 &&
        bounding.left >= 0 &&
        bounding.right <= (window.innerWidth || document.documentElement.clientWidth) &&
        bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight)
      ) {
        this.next();
      }
    }
    setTimeout(() => this.sliderScroll(), 4000);
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      const manager = new Manager(this.slider.nativeElement);
      manager.add(new Swipe({direction: DIRECTION_HORIZONTAL, threshold: 0}));
      manager.on('swipeleft', (event) => this.next());
      manager.on('swiperight', (event) => this.prev());
    }
  }

  next(): void {
    if (!this.stopScroll) {
      this.stopScroll = true;
      this.current = (this.current + 1) % this.slides.length;
      setTimeout(() => {
        this.stopScroll = false;
      }, 1);
    }

    this.cdr.markForCheck();
  }

  prev() {
    if (!this.stopScroll) {
      this.stopScroll = true;
      if (this.current === 0) {
        this.current = this.slides.length - 1;
      } else {
        this.current--;
      }
      setTimeout(() => {
        this.stopScroll = false;
      }, 1);
    }
    this.cdr.markForCheck();
  }

}
